/**
 * User API Adapter
 * Handles conversion between API responses and User model
 */

import { User, UserData } from '../User';
import { platformApi } from '@/services/platformApi';

export class UserApiAdapter {
  /**
   * Convert API response to User model
   */
  static fromApiResponse(apiData: any): User {
    const userData: UserData = {
      _id: apiData._id || apiData.id || '',
      username: apiData.username || apiData.email || '',
      email: apiData.email,
      profile: this.mapProfile(apiData),
      roles: apiData.roles || [],
      balances: apiData.balances || {},
      otherProfiles: apiData.otherProfiles || {},
      hasAdminAccess: apiData.hasAdminAccess || false,
      isCustomerSupport: apiData.isCustomerSupport || false,
      createdAt: apiData.createdAt,
      updatedAt: apiData.updatedAt,
      lastLogin: apiData.lastLogin,
      isGuest: apiData.isGuest || false
    };
    
    return new User(userData);
  }

  /**
   * Convert User model to API payload
   */
  static toApiPayload(user: User): any {
    const data = user.toJSON();
    
    return {
      _id: data._id,
      username: data.username,
      email: data.email,
      profile: data.profile,
      roles: data.roles,
      balances: data.balances,
      otherProfiles: data.otherProfiles,
      hasAdminAccess: data.hasAdminAccess,
      isCustomerSupport: data.isCustomerSupport
    };
  }

  /**
   * Map profile data from API
   */
  private static mapProfile(apiData: any): UserData['profile'] {
    const profile = apiData.profile || {};
    
    return {
      firstName: profile.firstName || apiData.firstName,
      lastName: profile.lastName || apiData.lastName,
      phone: profile.phone || apiData.phone,
      email: profile.email || apiData.email,
      avatar: profile.avatar || apiData.avatar,
      bio: profile.bio,
      dateOfBirth: profile.dateOfBirth,
      address: profile.address || apiData.address
    };
  }

  /**
   * Fetch user from API
   */
  static async fetchUser(userId?: string): Promise<User> {
    try {
      const response = userId 
        ? await platformApi.admin.getUser(userId)
        : await platformApi.getProfile();
      
      return this.fromApiResponse(response);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      throw error;
    }
  }

  /**
   * Save user to API
   */
  static async saveUser(user: User): Promise<User> {
    try {
      const payload = this.toApiPayload(user);
      const response = await platformApi.profile.updateProfile(payload);
      return this.fromApiResponse(response);
    } catch (error) {
      console.error('Failed to save user:', error);
      throw error;
    }
  }

  /**
   * Update specific user fields
   */
  static async updateUserFields(userId: string, updates: Partial<UserData>): Promise<User> {
    try {
      // Use PATCH for partial updates
      const response = await platformApi.patch('/profile', updates);
      return this.fromApiResponse(response);
    } catch (error) {
      console.error('Failed to update user fields:', error);
      throw error;
    }
  }

  /**
   * Add role to user
   */
  static async addUserRole(userId: string, role: string): Promise<User> {
    try {
      const response = await platformApi.post('/profile/roles/add', { role });
      return this.fromApiResponse(response);
    } catch (error) {
      console.error('Failed to add role:', error);
      throw error;
    }
  }

  /**
   * Remove role from user
   */
  static async removeUserRole(userId: string, role: string): Promise<User> {
    try {
      const response = await platformApi.post('/profile/roles/remove', { role });
      return this.fromApiResponse(response);
    } catch (error) {
      console.error('Failed to remove role:', error);
      throw error;
    }
  }

  /**
   * Update user balances
   */
  static async updateBalances(userId: string, balances: UserData['balances']): Promise<User> {
    try {
      // This would typically be done through a transaction API
      const response = await platformApi.admin.updateUserBalance(
        userId,
        Object.keys(balances)[0], // Primary currency
        Object.values(balances)[0], // Amount
        'set'
      );
      return this.fromApiResponse(response);
    } catch (error) {
      console.error('Failed to update balances:', error);
      throw error;
    }
  }

  /**
   * Create optimistic update
   */
  static createOptimisticUpdate(user: User, updates: Partial<UserData>): User {
    const currentData = user.toJSON();
    const optimisticData = {
      ...currentData,
      ...updates,
      profile: {
        ...currentData.profile,
        ...(updates.profile || {})
      },
      otherProfiles: {
        ...currentData.otherProfiles,
        ...(updates.otherProfiles || {})
      },
      updatedAt: new Date().toISOString()
    };
    
    return new User(optimisticData);
  }

  /**
   * Batch update multiple fields
   */
  static async batchUpdate(userId: string, updates: {
    profile?: Partial<UserData['profile']>;
    roles?: { add?: string[]; remove?: string[] };
    balances?: UserData['balances'];
  }): Promise<User> {
    try {
      const promises: Promise<any>[] = [];
      
      // Update profile if provided
      if (updates.profile) {
        promises.push(
          platformApi.patch('/profile', updates.profile)
        );
      }
      
      // Update roles if provided
      if (updates.roles?.add) {
        updates.roles.add.forEach(role => {
          promises.push(
            platformApi.post('/profile/roles/add', { role })
          );
        });
      }
      
      if (updates.roles?.remove) {
        updates.roles.remove.forEach(role => {
          promises.push(
            platformApi.post('/profile/roles/remove', { role })
          );
        });
      }
      
      // Wait for all updates
      await Promise.all(promises);
      
      // Fetch updated user
      return this.fetchUser(userId);
    } catch (error) {
      console.error('Failed to batch update user:', error);
      throw error;
    }
  }

  /**
   * Sync user with backend
   */
  static async syncUser(user: User): Promise<{ user: User; hasChanges: boolean }> {
    try {
      const backendUser = await this.fetchUser(user.id);
      const backendData = backendUser.toJSON();
      const localData = user.toJSON();
      
      // Check if there are differences
      const hasChanges = JSON.stringify(backendData) !== JSON.stringify(localData);
      
      return {
        user: backendUser,
        hasChanges
      };
    } catch (error) {
      console.error('Failed to sync user:', error);
      throw error;
    }
  }

  /**
   * Validate user data before save
   */
  static validateForSave(user: User): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];
    const data = user.toJSON();
    
    if (!data._id) {
      errors.push('User ID is required');
    }
    
    if (!data.username && !data.email) {
      errors.push('Username or email is required');
    }
    
    // Validate email format if provided
    if (data.email && !this.isValidEmail(data.email)) {
      errors.push('Invalid email format');
    }
    
    // Validate phone format if provided
    if (data.profile?.phone && !this.isValidPhone(data.profile.phone)) {
      errors.push('Invalid phone format');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }

  private static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private static isValidPhone(phone: string): boolean {
    // Basic phone validation - can be enhanced
    const phoneRegex = /^\+?[\d\s-()]+$/;
    return phoneRegex.test(phone) && phone.replace(/\D/g, '').length >= 10;
  }
}
